import {
  type PositionalWeekday,
  type RRuleDay,
  type RRuleFrequency,
  type RecurrenceRuleDescriptor,
  formatRecurrenceDescriptor,
  parseRecurrenceDescriptor,
} from '@zag/calendar/models';

/** Positional prefixes exposed in the monthly/yearly "nth weekday" builder. */
export const NTH_POSITIONS = [
  { value: 1, labelKey: 'calendars.rrule.positions.first' },
  { value: 2, labelKey: 'calendars.rrule.positions.second' },
  { value: 3, labelKey: 'calendars.rrule.positions.third' },
  { value: 4, labelKey: 'calendars.rrule.positions.fourth' },
  { value: -1, labelKey: 'calendars.rrule.positions.last' },
] as const;

export const ALL_WEEKDAYS: RRuleDay[] = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];

export const MONTHS_1_TO_12 = Array.from({ length: 12 }, (_, i) => i + 1);

export const DAYS_1_TO_31 = Array.from({ length: 31 }, (_, i) => i + 1);

/** Indicates whether the editor is composing its end bound as COUNT, UNTIL or neither. */
export type EndMode = 'never' | 'count' | 'until';

/** How a monthly/yearly rule selects days. */
export type MonthlyRepeatMode = 'day' | 'weekday';

/** Default empty descriptor used when the editor mounts with an empty value. */
export function emptyDescriptor(): RecurrenceRuleDescriptor {
  return { frequency: 'daily' };
}

/** True when the descriptor is the same as the empty default. */
export function isEmptyDescriptor(desc: RecurrenceRuleDescriptor): boolean {
  return (
    desc.frequency === 'daily' && !desc.byDay?.length && desc.interval === undefined && desc.count === undefined && desc.until === undefined
  );
}

/** Safely parse an RRULE string, falling back to an empty descriptor on failure. */
export function safeParseDescriptor(rule: string): {
  descriptor: RecurrenceRuleDescriptor;
  error: string | null;
} {
  if (!rule.trim()) return { descriptor: emptyDescriptor(), error: null };
  try {
    return { descriptor: parseRecurrenceDescriptor(rule), error: null };
  } catch (e) {
    return { descriptor: emptyDescriptor(), error: e instanceof Error ? e.message : String(e) };
  }
}

/** Compute the end-mode for a descriptor. */
export function deriveEndMode(desc: RecurrenceRuleDescriptor): EndMode {
  if (desc.count !== undefined && desc.count > 0) return 'count';
  if (desc.until) return 'until';
  return 'never';
}

/**
 * Inspect a monthly/yearly descriptor and decide which repeat-mode tab the
 * UI should present. The tab defaults to 'day' when neither is set so that
 * the user starts from the simplest case.
 */
export function deriveMonthlyRepeatMode(desc: RecurrenceRuleDescriptor): MonthlyRepeatMode {
  const hasPositionalByDay = desc.byDay?.some(d => d.n !== undefined);
  const hasBySetPos = desc.bySetPos?.length;
  if (hasPositionalByDay || hasBySetPos) return 'weekday';
  return 'day';
}

/** Toggle a weekday in a list. Returns a new array, sorted by week order. */
export function toggleWeekday(byDay: PositionalWeekday[] | undefined, day: RRuleDay): PositionalWeekday[] {
  const list = byDay ?? [];
  const existing = list.findIndex(d => d.day === day && d.n === undefined);
  if (existing >= 0) {
    return list.filter((_, i) => i !== existing);
  }
  return sortWeekdays([...list, { day }]);
}

/** Toggle a single number in a list (for BYMONTH, BYMONTHDAY, BYHOUR, etc). */
export function toggleNumber(list: number[] | undefined, value: number): number[] {
  const current = list ?? [];
  if (current.includes(value)) return current.filter(v => v !== value);
  return [...current, value].sort((a, b) => a - b);
}

/** Stable weekday ordering for presentation. */
export function sortWeekdays(list: PositionalWeekday[]): PositionalWeekday[] {
  return [...list].sort((a, b) => {
    const ai = ALL_WEEKDAYS.indexOf(a.day);
    const bi = ALL_WEEKDAYS.indexOf(b.day);
    if (ai !== bi) return ai - bi;
    return (a.n ?? 0) - (b.n ?? 0);
  });
}

/** Parse a comma/space separated integer list. Invalid tokens are dropped. */
export function parseIntList(raw: string, opts?: { min?: number; max?: number; allowNegative?: boolean }): number[] {
  if (!raw.trim()) return [];
  const result: number[] = [];
  const tokens = raw.split(/[\s,]+/).filter(Boolean);
  for (const token of tokens) {
    const n = Number(token);
    if (!Number.isInteger(n)) continue;
    if (opts?.min !== undefined && n < opts.min) continue;
    if (opts?.max !== undefined && n > opts.max) continue;
    if (!opts?.allowNegative && n < 0) continue;
    if (!result.includes(n)) result.push(n);
  }
  return result.sort((a, b) => a - b);
}

/** Serialize an integer list for display in an `<input type="text">` advanced field. */
export function serializeIntList(list: number[] | undefined): string {
  return (list ?? []).join(', ');
}

/**
 * Given a descriptor being edited in the visual editor, ensure frequency
 * transitions don't leave behind incompatible BY-fields.
 *
 * Example: switching WEEKLY → MONTHLY should clear plain BYDAY chips but keep
 * BYMONTHDAY; switching MONTHLY → YEARLY keeps the monthly selectors since
 * they remain meaningful.
 */
export function normalizeAfterFrequencyChange(prev: RecurrenceRuleDescriptor, nextFrequency: RRuleFrequency): RecurrenceRuleDescriptor {
  const next: RecurrenceRuleDescriptor = { ...prev, frequency: nextFrequency };

  if (nextFrequency === 'daily' || nextFrequency === 'hourly' || nextFrequency === 'minutely' || nextFrequency === 'secondly') {
    delete next.byDay;
    delete next.byMonthDay;
    delete next.byMonth;
    delete next.bySetPos;
  }

  if (nextFrequency === 'weekly') {
    delete next.byMonthDay;
    delete next.byMonth;
    delete next.bySetPos;
    if (!next.byDay?.length) next.byDay = [{ day: 'MO' }];
  }

  if (nextFrequency === 'monthly') {
    delete next.byMonth;
  }

  // Yearly keeps all byMonth/byMonthDay/byDay selectors as they remain valid.

  return next;
}

/** Build the final RRULE string from a descriptor, handling invalid states. */
export function descriptorToRrule(desc: RecurrenceRuleDescriptor): string {
  return formatRecurrenceDescriptor(desc);
}
